import React from "react";

const posts = [
  {
    category: "Feature Documentation",
    title: "Starburst AI Data Assistant (AIDA)",
    year: "2026",
    approach:
      "AIDA covers a lot of ground: natural language querying, SQL generation, live data execution, data analysis, and visualization, across three distinct user personas with different expectations for how results should be presented.\n\n I started with persona mapping before touching any content structure. Analysts and business users needed task-based guidance. Admins needed configuration and access control requirements without having to read through end-user content to find them. Separating those concerns early made the structure easier to navigate for everyone.\n\nThe trickiest part was the conceptual layer. NL-to-SQL isn't intuitive, and users need enough context to understand what AIDA can and can't do. Getting that balance right meant the procedural sections could stay focused on tasks rather than stopping to explain how the feature works.",
    url: "https://docs.starburst.io/latest/starburst-ai/aida-ai.html",
  },
  {
    category: "Configuration Reference",
    title: "Configure Starburst AI",
    year: "2026",
    approach:
      "Starburst AI integrates with multiple LLM providers, each with its own authentication method and configuration requirements. The audience is platform administrators who need to get this working in production, so there's not much room for ambiguity.\n\nI organized configuration properties around the sequence administrators actually follow: pick a provider, set up authentication, configure model parameters, manage access. That's different from how the feature is structured internally, but it matches how admins think about setup.\n\nProvider-specific differences, like how AWS Bedrock handles credentials compared to OpenAI's API key model, are documented in context rather than consolidated into a comparison table that's easy to miss.",
    url: "https://docs.starburst.io/latest/starburst-ai/configuration-ai.html",
  },
  {
    category: "How-to Guide",
    title: "Share data products",
    year: "2025",
    approach:
      "This feature spans two separate clusters, which is the first thing that makes documentation tricky. The publisher and subscriber clusters each have their own configuration, role definitions, and access control requirements, and neither makes much sense without understanding how they relate to each other.\n\nI structured the guide around the complete workflow rather than splitting content by cluster. Admins need the conceptual before the individual steps land. Prerequisites are consolidated at the start so nothing is discovered mid-task.\n\nThe dependency between clusters also shaped how I documented access control. Subscriber permissions depend on what the publisher exposes, so I documented those together rather than treating them as separate topics.",
    url: "https://docs.starburst.io/latest/data-products/share-data-products.html",
  },
  {
    category: "Content Strategy",
    title: "Improved connectors documentation consolidation",
    year: "2024",
    approach:
      "The Starburst-improved connectors had a straightforward problem: enterprise-specific features were on separate pages from the core connector reference page. To understand any one connector fully, users had to look in two places.\n\nI audited every improved connector page, identified what was duplicated or missing, and built a structure that could apply consistently across all of them. The goal was to make each connector page self-contained. The Delta Lake connector is a good example of the end result.\n\nApplying the same structure across a large number of pages was the bulk of the work.",
    url: "https://docs.starburst.io/latest/connector/delta-lake.html",
  },
  {
    category: "Content Engineering",
    title: "SEP documentation migration: RST to MyST Markdown",
    year: "2023",
    approach:
      "The SEP documentation set was in reStructuredText when I took this on. Moving to MyST Markdown made the docs easier for contributors to work with and brought the toolchain in line with the rest of the Starburst documentation workflow.\n\nIt wasn't a mechanical conversion. RST and MyST handle directives, cross-references, and some structural elements differently, so each page had to be reviewed rather than just run through a script. The build configuration was updated alongside the content to keep the output consistent.\n\nThe end result was a documentation set on a single authoring format, which made ongoing maintenance and content switching between doc sets significantly easier.",
    url: null,
  },
];

const Writing = () => {
  return (
    <div name="writing" className="w-full bg-[#0a192f] text-white py-16">
      <div className="max-w-[800px] mx-auto px-4">
        <div className="pb-10 flex flex-col items-center">
          <p className="text-4xl font-bold inline border-b-4 text-gray-300 border-cyan-500">
            Writing
          </p>
          <p className="py-6 text-xl text-center text-gray-400 max-w-[600px]">
            Technical writing samples and documentation links.
          </p>
        </div>

        <div className="flex flex-col gap-10">
          {posts.map((post, i) => (
            <article key={i} className="border-l-4 border-cyan-600 pl-6 group">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
                  {post.category}
                </span>
                <span className="text-gray-600 text-xs">{post.year}</span>
              </div>

              <h2 className="text-xl font-bold text-gray-100 mb-3 leading-snug group-hover:text-amber-300 transition-colors duration-200">
                {post.title}
              </h2>

              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">
                Approach
              </p>

              <div className="text-gray-400 text-sm leading-relaxed mb-4 flex flex-col gap-3">
                {post.approach.split("\n\n").map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
              </div>

              {post.url && (
                <a
                  href={post.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-cyan-400 group-hover:text-amber-300 transition-colors duration-200"
                >
                  Read the docs →
                </a>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Writing;
